import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Col,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faCheck,
  faClock,
  faCreditCard,
  faExclamationTriangle,
  faFileInvoiceDollar,
  faFilePdf,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import SoftBadge from 'components/common/SoftBadge';
import { BillingStatus } from './OrganizationBillingLayout';
import {
  formatBillingDate,
  formatInvoiceDescription,
  formatUsdCents,
  sponsorshipReasonLabel
} from 'util/organizationBillingFormat';

const invoiceTypeLabel = type =>
  ({
    Subscription: 'Subscription',
    HostedAccount: 'Hosted accounts',
    NoCharges: 'No charges',
    Mixed: 'Subscription + hosted accounts'
  }[type] ?? 'Other');

const formatTierLabel = tierCode => {
  if (!tierCode) return null;
  const str = String(tierCode);
  if (str.startsWith('sponsored-standard')) return 'Standard rate';
  if (str.startsWith('sponsored-volume') || str.startsWith('volume'))
    return 'Volume tier';
  if (str.startsWith('custom')) return 'Custom tier';
  return null;
};

const formatReasonBadge = reasonCode => {
  if (!reasonCode || reasonCode === 'standard-price') return null;
  const isPositive =
    reasonCode.includes('qualifying') || reasonCode.includes('enabled');
  // An invoice records what was charged; the rate is already on the line, so the badge only states the sharing status.
  const label = isPositive ? 'Sharing' : sponsorshipReasonLabel(reasonCode);
  return (
    <SoftBadge bg={isPositive ? 'success' : 'secondary'} pill>
      {label}
    </SoftBadge>
  );
};

export const OrganizationInvoiceDetailModal = ({
  show,
  onHide,
  detail,
  detailData,
  downloading,
  downloadError,
  onDownloadPdf,
  onPayNow,
  canPay
}) => {
  const invoice = detailData?.Invoice;
  const lines = detailData?.Lines ?? [];
  const payments = detailData?.Payments ?? [];
  const isFetching = detail?.isFetching && !detailData;
  const error = detail?.error;

  const errorText =
    error?.data?.Data ??
    error?.data?.data ??
    error?.error ??
    'Unable to load invoice details.';

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="invoice-detail-modal"
    >
      <Modal.Header closeButton className="px-4 py-3 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <Modal.Title className="fs-1 fw-bold text-900 mb-0">
            Invoice details
          </Modal.Title>
          {invoice?.Status && <BillingStatus>{invoice.Status}</BillingStatus>}
        </div>
      </Modal.Header>

      <Modal.Body className="px-4 py-4">
        {isFetching && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" role="status" />
            <div className="text-600 fs--1 mt-3">
              Loading invoice details...
            </div>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="d-flex align-items-center mb-3">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="me-2 flex-shrink-0"
            />
            <div className="fs--1">{errorText}</div>
          </Alert>
        )}

        {downloadError && (
          <Alert variant="danger" className="d-flex align-items-center mb-3">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="me-2 flex-shrink-0"
            />
            <div className="fs--1">{downloadError}</div>
          </Alert>
        )}

        {invoice && (
          <>
            {/* Summary */}
            <div className="rounded-3 bg-100 border p-3 p-md-4 mb-4">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div className="min-w-0">
                  <div className="text-500 fs--2 text-uppercase fw-semi-bold mb-1">
                    Invoice Number
                  </div>
                  <div
                    className="text-900 fw-bold font-monospace fs-0 text-break"
                    style={{ wordBreak: 'break-all' }}
                  >
                    {invoice.InvoiceNumber}
                  </div>
                  {invoice.InvoiceType && (
                    <div className="text-600 fs--2 mt-1">
                      {invoiceTypeLabel(invoice.InvoiceType)}
                    </div>
                  )}
                </div>

                <div className="text-sm-end">
                  <div className="text-500 fs--2 text-uppercase fw-semi-bold mb-1">
                    {invoice.Status === 'Paid' ? 'Total Paid' : 'Total Due'}
                  </div>
                  <div className="fs-3 fw-bold text-900 lh-1">
                    {formatUsdCents(invoice.TotalCents)}
                  </div>
                  <div className="text-500 fs--2 mt-1">
                    {invoice.Currency || 'USD'}
                  </div>
                </div>
              </div>

              <hr className="my-3 text-300" />

              <Row className="gy-2 gx-4">
                <Col xs={12} sm={6}>
                  <div className="text-500 fs--2 text-uppercase fw-semi-bold mb-1">
                    Issued
                  </div>
                  <div className="text-900 fs--1 fw-semi-bold">
                    {invoice.FinalizedUtc || invoice.CreatedUtc
                      ? formatBillingDate(
                          invoice.FinalizedUtc || invoice.CreatedUtc
                        )
                      : '—'}
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  {invoice.PaidUtc ? (
                    <>
                      <div className="text-500 fs--2 text-uppercase fw-semi-bold mb-1">
                        Payment
                      </div>
                      <div className="text-success fs--1 fw-semi-bold">
                        <FontAwesomeIcon icon={faCheck} className="me-1" />
                        Paid {formatBillingDate(invoice.PaidUtc)}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-500 fs--2 text-uppercase fw-semi-bold mb-1">
                        Due Date
                      </div>
                      <div className="text-900 fs--1 fw-semi-bold">
                        {invoice.DueDateUtc
                          ? formatBillingDate(invoice.DueDateUtc)
                          : 'Due on receipt'}
                      </div>
                    </>
                  )}
                </Col>
              </Row>
            </div>

            {/* Itemized Charges Section */}
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="text-uppercase text-700 fs--1 fw-bold mb-0">
                  Charges & Subscriptions
                </h6>
                <span className="text-500 fs--2">
                  {lines.length} line item{lines.length === 1 ? '' : 's'}
                </span>
              </div>

              <div className="table-responsive border rounded-3 overflow-hidden">
                <Table className="mb-0 fs--1" hover>
                  <thead className="bg-100 text-700 fs--2 text-uppercase">
                    <tr>
                      <th className="py-2 px-3 border-0">Description</th>
                      <th
                        className="py-2 px-3 border-0 text-center"
                        style={{ width: '85px' }}
                      >
                        Qty
                      </th>
                      <th
                        className="py-2 px-3 border-0 text-end"
                        style={{ width: '110px' }}
                      >
                        Rate
                      </th>
                      <th
                        className="py-2 px-3 border-0 text-end"
                        style={{ width: '120px' }}
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length > 0 ? (
                      lines.map(line => (
                        <tr key={line.GUID} className="align-middle">
                          <td className="py-3 px-3">
                            <div className="fw-semi-bold text-900">
                              {formatInvoiceDescription(line.Description)}
                            </div>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {formatTierLabel(line.TierCode) && (
                                <SoftBadge
                                  bg="light"
                                  className="text-700 border"
                                  pill
                                >
                                  {formatTierLabel(line.TierCode)}
                                </SoftBadge>
                              )}
                              {formatReasonBadge(line.ReasonCode)}
                            </div>
                          </td>
                          <td className="py-3 px-3 text-center text-700">
                            {line.Quantity}
                          </td>
                          <td className="py-3 px-3 text-end text-700">
                            {formatUsdCents(line.UnitAmountCents)}
                          </td>
                          <td className="py-3 px-3 text-end fw-bold text-900">
                            {formatUsdCents(line.AmountCents)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="py-4 text-center text-600">
                          No line items found for this invoice.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {lines.length > 0 && (
                    <tfoot className="border-top bg-100">
                      <tr className="border-top border-200">
                        <td
                          colSpan={3}
                          className="text-end py-2 px-3 fw-bold text-900 fs-0"
                        >
                          Total
                        </td>
                        <td className="text-end py-2 px-3 fw-bold text-primary fs-0">
                          {formatUsdCents(invoice.TotalCents)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </Table>
              </div>
            </div>

            {/* Payment History Section */}
            <div>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="text-uppercase text-700 fs--1 fw-bold mb-0">
                  Payment History
                </h6>
                {payments.length > 1 && (
                  <span className="badge bg-soft-secondary text-secondary rounded-pill">
                    {payments.length} attempts
                  </span>
                )}
              </div>

              {payments.length > 0 ? (
                <div className="border rounded-3 p-3">
                  {payments.map((payment, index) => {
                    const succeeded = payment.Status === 'Succeeded';
                    const failed = payment.Status === 'Failed';
                    const last = index === payments.length - 1;

                    return (
                      <div
                        key={payment.AttemptNumber}
                        className="d-flex align-items-start gap-3"
                      >
                        <div className="d-flex flex-column align-items-center">
                          <span
                            className={`d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-soft-${
                              succeeded
                                ? 'success'
                                : failed
                                ? 'danger'
                                : 'warning'
                            } text-${
                              succeeded
                                ? 'success'
                                : failed
                                ? 'danger'
                                : 'warning'
                            }`}
                            style={{ width: 34, height: 34 }}
                            aria-hidden="true"
                          >
                            <FontAwesomeIcon
                              icon={
                                succeeded ? faCheck : failed ? faTimes : faClock
                              }
                              size="sm"
                            />
                          </span>
                          {!last && (
                            <span
                              className="border-start border-300 flex-grow-1 my-1"
                              style={{ minHeight: 28, borderWidth: 2 }}
                            />
                          )}
                        </div>

                        <div
                          className={`d-flex flex-grow-1 justify-content-between align-items-start gap-3${
                            last ? '' : ' pb-3'
                          }`}
                        >
                          <div>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <span className="text-900 fw-bold">
                                Card payment
                              </span>
                              <BillingStatus>{payment.Status}</BillingStatus>
                              {payments.length > 1 && (
                                <span className="text-500 fs--2">
                                  Attempt {payment.AttemptNumber}
                                </span>
                              )}
                            </div>

                            {payment.CompletedUtc && (
                              <div className="text-600 fs--2 mt-1">
                                <FontAwesomeIcon
                                  icon={faCalendarAlt}
                                  className="me-1 text-400"
                                />
                                {succeeded ? 'Paid' : 'Attempted'} on{' '}
                                {formatBillingDate(payment.CompletedUtc)}
                              </div>
                            )}

                            {payment.FailureCode && (
                              <div className="alert alert-soft-danger py-1 px-2 mt-2 mb-1 fs--2 d-inline-block">
                                <FontAwesomeIcon
                                  icon={faExclamationTriangle}
                                  className="me-1"
                                />
                                {payment.FailureCode}
                              </div>
                            )}

                            {payment.NextRetryUtc && (
                              <div className="text-warning fs--2 mt-1 fw-semi-bold">
                                <FontAwesomeIcon
                                  icon={faClock}
                                  className="me-1"
                                />
                                Next automatic retry:{' '}
                                {formatBillingDate(payment.NextRetryUtc)}
                              </div>
                            )}
                          </div>

                          <div className="text-end">
                            <span className="fw-bold text-900 fs-0">
                              {formatUsdCents(payment.AmountCents)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="border rounded-3 p-3 text-center bg-light text-600 fs--1">
                  <FontAwesomeIcon icon={faClock} className="me-2 text-400" />
                  No payment attempts recorded for this invoice yet.
                </div>
              )}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex flex-wrap justify-content-end align-items-center border-top px-4 py-3">
        <div className="d-flex gap-2">
          {invoice?.GUID && (
            <Button
              variant="falcon-default"
              size="sm"
              disabled={Boolean(downloading)}
              onClick={() => onDownloadPdf(invoice.GUID)}
            >
              <FontAwesomeIcon icon={faFilePdf} className="me-1 text-danger" />
              {downloading === invoice.GUID ? 'Downloading...' : 'Download PDF'}
            </Button>
          )}
          {invoice?.Status === 'Open' && (
            <Button
              variant="primary"
              size="sm"
              disabled={!canPay}
              onClick={() => onPayNow(invoice)}
            >
              <FontAwesomeIcon icon={faCreditCard} className="me-1" />
              Pay now
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={onHide}>
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

OrganizationInvoiceDetailModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  detail: PropTypes.object,
  detailData: PropTypes.object,
  downloading: PropTypes.string,
  downloadError: PropTypes.string,
  onDownloadPdf: PropTypes.func.isRequired,
  onPayNow: PropTypes.func.isRequired,
  canPay: PropTypes.bool
};

export default OrganizationInvoiceDetailModal;
